// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { ArrowRight12 } from '@safecoin/icons';
import { Heading, Text } from '@mysten/ui';
import { type ColumnDef } from '@tanstack/react-table';
import clsx from 'clsx';
import { type ReactNode, useMemo, useState } from 'react';

import { COINS_API_URL, useCoinsList, type CoinSummary } from '~/hooks/useCoins';
import { CoinAvatar } from './CoinAvatar';
import {
	formatCreateTime,
	formatPercentChange,
	formatSupplyAmount,
	formatUsd,
	formatUsdPlain,
	formatVolumeParts,
} from './format';
import { PageLayout } from '~/components/Layout/PageLayout';
import { Banner } from '~/ui/Banner';
import { ReactComponent as InfoSvg } from '~/ui/icons/info_10x10.svg';
import { Link } from '~/ui/Link';
import { PlaceholderTable } from '~/ui/PlaceholderTable';
import { TableCard } from '~/ui/TableCard';
import { Tooltip } from '~/ui/Tooltip';

const PAGE_SIZE_OPTIONS = [20, 40, 60];

function HeaderWithInfo({ children, tip }: { children: ReactNode; tip: string }) {
	return (
		<div className="flex items-center gap-1">
			{children}
			<Tooltip tip={tip}>
				<InfoSvg />
			</Tooltip>
		</div>
	);
}

const columns: ColumnDef<CoinSummary>[] = [
	{
		header: 'Name',
		accessorKey: 'coinType',
		cell: (props) => {
			const coin = props.row.original;
			const name = coin.name || coin.symbol || 'Unknown Coin';
			return (
				<Link to={`/coin/${encodeURIComponent(coin.coinType)}`}>
					<div className="flex items-center gap-3 py-1">
						<CoinAvatar coin={coin} name={name} />
						<div className="flex flex-col">
							<Text variant="bodySmall/semibold" color="steel-darker">
								{name}
							</Text>
							{coin.symbol && (
								<Text variant="captionSmall/medium" color="steel-dark">
									{coin.symbol}
								</Text>
							)}
						</div>
					</div>
				</Link>
			);
		},
	},
	{
		header: 'Price',
		accessorKey: 'priceUsd',
		cell: (props) => (
			<Text variant="bodySmall/medium" color="steel-darker">
				{props.row.original.priceUsd != null ? formatUsd(props.row.original.priceUsd) : '--'}
			</Text>
		),
	},
	{
		id: 'volume',
		accessorKey: 'volumeUsd24h',
		header: () => (
			<div className="flex items-center gap-1">
				<span>Volume (24h)</span>
				<ArrowRight12 fill="currentColor" className="rotate-90 text-steel-darker" />
			</div>
		),
		cell: (props) => {
			const parts = formatVolumeParts(props.row.original.volumeUsd24h);
			if (!parts) return <Text variant="bodySmall/medium">--</Text>;
			return (
				<Text variant="bodySmall/medium" color="steel-darker">
					{parts.whole}
					<span className="text-steel-dark">.{parts.fraction}</span>
				</Text>
			);
		},
	},
	{
		header: 'Volume Change (24h)',
		accessorKey: 'volumeChange24h',
		cell: (props) => {
			const change = formatPercentChange(props.row.original.volumeChange24h);
			if (!change) return <Text variant="bodySmall/medium">--</Text>;
			return (
				<span
					className={clsx(
						'text-bodySmall font-medium',
						change.positive ? 'text-success-dark' : 'text-issue-dark',
					)}
				>
					{change.text}
				</span>
			);
		},
	},
	{
		id: 'supply',
		accessorKey: 'totalSupply',
		header: () => <HeaderWithInfo tip="Total supply and its USD value">Supply</HeaderWithInfo>,
		cell: (props) => {
			const coin = props.row.original;
			const amount = formatSupplyAmount(coin.totalSupply, coin.decimals);
			const usd = formatUsdPlain(coin.fdvUsd ?? coin.marketCapUsd);
			return (
				<div className="flex flex-col">
					<Text variant="bodySmall/medium" color="steel-darker">
						{amount ?? '--'}
						{amount && coin.symbol ? (
							<span className="text-steel-dark"> {coin.symbol}</span>
						) : null}
					</Text>
					<Text variant="captionSmall/medium" color="steel-dark">
						{usd ? `${usd} USD` : '--'}
					</Text>
				</div>
			);
		},
	},
	{
		id: 'holders',
		accessorKey: 'holders',
		header: () => (
			<HeaderWithInfo tip="Distinct addresses holding this coin">Holders</HeaderWithInfo>
		),
		cell: (props) => (
			<Text variant="bodySmall/medium" color="steel-darker">
				{Number(props.row.original.holders).toLocaleString('en-US')}
			</Text>
		),
	},
	{
		header: 'Create Time',
		accessorKey: 'createdAtMs',
		cell: (props) => (
			<Text variant="bodySmall/medium" color="steel-dark">
				{formatCreateTime(props.row.original.createdAtMs)}
			</Text>
		),
	},
];

const PLACEHOLDER_HEADINGS = [
	'Name',
	'Price',
	'Volume (24h)',
	'Volume Change (24h)',
	'Supply',
	'Holders',
	'Create Time',
];
const PLACEHOLDER_WIDTHS = ['24%', '10%', '16%', '16%', '16%', '10%', '8%'];

// Build the compact page-number sequence, e.g. [1,2,3,4,5,'gap',25].
function getPageTokens(current: number, total: number): (number | 'gap')[] {
	if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
	if (current <= 4) return [1, 2, 3, 4, 5, 'gap', total];
	if (current >= total - 3) return [1, 'gap', total - 4, total - 3, total - 2, total - 1, total];
	return [1, 'gap', current - 1, current, current + 1, 'gap', total];
}

function NumberedPagination({
	current,
	total,
	onChange,
}: {
	current: number;
	total: number;
	onChange(page: number): void;
}) {
	if (total <= 1) return null;
	const tokens = getPageTokens(current, total);
	return (
		<div className="flex items-center gap-1.5">
			{tokens.map((token, index) =>
				token === 'gap' ? (
					<span key={`gap-${index}`} className="px-1 text-steel-dark">
						…
					</span>
				) : (
					<button
						key={token}
						type="button"
						onClick={() => onChange(token)}
						className={clsx(
							'h-8 min-w-[2rem] rounded-md px-2 text-bodySmall font-medium',
							token === current
								? 'bg-gray-40 text-steel-darker'
								: 'text-steel-dark hover:bg-gray-40/60',
						)}
					>
						{token}
					</button>
				),
			)}
			<button
				type="button"
				aria-label="Next page"
				disabled={current >= total}
				onClick={() => onChange(current + 1)}
				className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-45 text-steel-dark shadow-xs hover:bg-gray-40/60 disabled:opacity-40"
			>
				<ArrowRight12 fill="currentColor" />
			</button>
		</div>
	);
}

export function Coins() {
	const { data, isPending, isError } = useCoinsList();
	const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
	const [page, setPage] = useState(1);

	// Sort the full list by 24h volume (desc) so pagination reflects the ranking.
	const coins = useMemo(() => {
		const list = data?.coins ?? [];
		return [...list].sort((a, b) => (b.volumeUsd24h ?? -1) - (a.volumeUsd24h ?? -1));
	}, [data?.coins]);

	const pageCount = Math.max(Math.ceil(coins.length / pageSize), 1);
	const currentPage = Math.min(page, pageCount);
	const pageCoins = coins.slice((currentPage - 1) * pageSize, currentPage * pageSize);

	const header = (
		<div className="flex items-center justify-between">
			<Heading as="h1" variant="heading4/semibold" color="steel-darker">
				Coins
			</Heading>
			<div className="flex items-center gap-4">
				<a
					href={`${COINS_API_URL}/coins`}
					target="_blank"
					rel="noreferrer"
					className="font-mono text-bodySmall font-medium text-steel-dark hover:text-hero-dark"
				>
					{'{API}'}
				</a>
				<button
					type="button"
					className="flex items-center gap-1 rounded-lg bg-hero-dark/10 px-3 py-1.5 text-bodySmall font-semibold text-hero-dark hover:bg-hero-dark/20"
				>
					<span className="text-base leading-none">+</span> Submit Coin
				</button>
			</div>
		</div>
	);

	if (isError) {
		return (
			<PageLayout
				content={
					<div className="flex flex-col gap-5">
						{header}
						<Banner variant="error" fullWidth>
							Failed to load coins. The coins API may be unavailable.
						</Banner>
					</div>
				}
			/>
		);
	}

	const toolbar = (
		<div className="flex flex-col gap-4 border-b border-gray-45 pb-4 md:flex-row md:items-center md:justify-between">
			<div className="flex flex-col">
				<Text variant="bodySmall/semibold" color="steel-darker">
					{data ? `${data.count.toLocaleString('en-US')} found` : '--'}
				</Text>
				<Text variant="captionSmall/medium" color="steel-dark">
					Showing top {coins.length.toLocaleString('en-US')}
				</Text>
			</div>

			<NumberedPagination current={currentPage} total={pageCount} onChange={setPage} />

			<div className="flex items-center gap-2">
				<Text variant="bodySmall/medium" color="steel-dark">
					Show
				</Text>
				<select
					className="form-select rounded-md border border-gray-45 px-3 py-1.5 pr-8 text-bodySmall font-medium leading-[1.2] text-steel-dark shadow-button"
					value={pageSize}
					onChange={(e) => {
						setPageSize(Number(e.target.value));
						setPage(1);
					}}
				>
					{PAGE_SIZE_OPTIONS.map((size) => (
						<option key={size} value={size}>
							{size}
						</option>
					))}
				</select>
			</div>
		</div>
	);

	return (
		<PageLayout
			content={
				<div className="flex flex-col gap-5">
					{header}
					<div className="flex flex-col gap-4">
						{toolbar}
						{isPending ? (
							<PlaceholderTable
								rowCount={pageSize}
								rowHeight="32px"
								colHeadings={PLACEHOLDER_HEADINGS}
								colWidths={PLACEHOLDER_WIDTHS}
							/>
						) : (
							<TableCard data={pageCoins} columns={columns} sortTable />
						)}
					</div>
				</div>
			}
		/>
	);
}
